import { apis as rideApis } from './ride/apis';
import { statusCodes as rideStatusCodes } from './ride/statusCodes';
import { rideWebhook } from './ride/webhook';
import { apis as deliveryApis } from './delivery/apis';
import { statusCodes as deliveryStatusCodes } from './delivery/statusCodes';
import { deliveryWebhook } from './delivery/webhook';

export const products = [
  {
    id: 'ride',
    slug: 'wiride',
    name: 'WiRide',
    headerLabel: 'Ride API',
    logo: '/Wi_Ride_logo.png',
    logoAlt: 'WiRide by Wiyak',
    logoWide: true,
    tagline: 'Corporate ride booking',
    description: "Integrate Wiyak's ride-booking platform into your product. Create, manage, and cancel bookings programmatically.",
    color: '#111827',
    emoji: '🚗',
    environments: [
      { id: 'staging', label: 'Staging', url: 'https://development.wiyak.com/WiyakNode', color: '#f59e0b' },
      { id: 'live', label: 'Live', url: 'https://api.wiyak.com', color: '#22c55e' },
      { id: 'custom', label: 'Custom', url: '', color: '#6366f1' },
    ],
    apis: rideApis,
    statusCodes: rideStatusCodes,
    webhook: rideWebhook,
    gettingStarted: {
      title: 'Wiyak Corporate',
      subtitle: 'API Reference',
      steps: [
        { num: '01', title: 'Get Your Bearer Key', desc: 'Contact the Wiyak team to receive your unique bearer key. This permanent credential identifies your corporate account.', color: '#6366f1' },
        { num: '02', title: 'Authenticate', desc: 'Call Corporate Authorize with your bearer key. Save the returned JWT token — it grants access to all other endpoints.', color: '#8b5cf6' },
        { num: '03', title: 'Use the Token', desc: 'Send bearerkey in the header and the JWT token in the request body for all subsequent API calls.', color: '#22c55e' },
        { num: '04', title: 'Register Webhook', desc: 'Optionally register your HTTPS callback URL during login to receive real-time booking status updates.', color: '#f97316' },
      ],
      endpointCards: [
        { id: 'login', emoji: '🔐', name: 'Authorize', desc: 'Authenticate & get token', color: '#6366f1' },
        { id: 'estimated-fare', emoji: '💰', name: 'Estimated Fare', desc: 'Get price per car type', color: '#8b5cf6' },
        { id: 'nearest-driver', emoji: '🚗', name: 'Nearest Driver', desc: 'Check driver ETA', color: '#3b82f6' },
        { id: 'create-order', emoji: '➕', name: 'Create Order', desc: 'Book a ride', color: '#22c55e' },
        { id: 'booking-list', emoji: '📋', name: 'Booking List', desc: 'List or fetch bookings', color: '#06b6d4' },
        { id: 'edit-order', emoji: '✏️', name: 'Edit Order', desc: 'Modify a booking', color: '#f59e0b' },
        { id: 'cancel-order', emoji: '❌', name: 'Cancel Order', desc: 'Cancel a booking', color: '#ef4444' },
      ],
      authItems: [
        { label: 'Login API', val: 'bearerkey (in header)', color: '#6366f1' },
        { label: 'All Other APIs', val: 'bearerkey (header) + token (body)', color: '#22c55e' },
        { label: 'Token Type', val: 'JWT — JSON Web Token', color: '#8b5cf6' },
        { label: 'Webhook Security', val: 'HMAC-SHA256 · x-secure-signature', color: '#f97316' },
      ],
    },
  },
  {
    id: 'delivery',
    slug: 'wideliver',
    name: 'WiDeliver',
    headerLabel: 'Delivery API',
    logo: '/Wi_Deliver_logo.png',
    logoAlt: 'WiDeliver by Wiyak',
    logoWide: true,
    tagline: 'Restaurant & grocery dispatch',
    description: 'Dispatch restaurant orders, manage branches, quote fares, and track drivers with the Wiyak Delivery REST API.',
    color: '#00a8d0',
    emoji: '🍔',
    environments: [
      { id: 'sandbox', label: 'Sandbox', url: 'https://sandbox.wiyak.delivery/v4', color: '#f59e0b' },
      { id: 'live', label: 'Live', url: 'https://api.wiyak.delivery/v4', color: '#22c55e' },
      { id: 'custom', label: 'Custom', url: '', color: '#6366f1' },
    ],
    apis: deliveryApis,
    statusCodes: deliveryStatusCodes,
    webhook: deliveryWebhook,
    gettingStarted: {
      title: 'Wiyak Delivery',
      subtitle: 'API Reference',
      steps: [
        { num: '01', title: 'Get Your API Key', desc: 'Your restaurant bearerkey is more than 64 digits. Keep it secret — it authenticates every request.', color: '#f97316' },
        { num: '02', title: 'Authorize', desc: 'Call Authorize with your bearerkey. Save the JWT — it is valid for 365 days and required on every later call.', color: '#8b5cf6' },
        { num: '03', title: 'Quote then Dispatch', desc: 'Get Estimated Price for the drop address, then Create Order with branch, customer, and payment details.', color: '#22c55e' },
        { num: '04', title: 'Listen to Webhooks', desc: 'Register web_hook_url on Authorize to receive pending, searching, accept, onroute, completed, and cancel events.', color: '#6366f1' },
      ],
      endpointCards: [
        { id: 'login', emoji: '🔐', name: 'Authorize', desc: 'Get a 365-day JWT', color: '#6366f1' },
        { id: 'estimated-fare', emoji: '💰', name: 'Estimated Price', desc: 'Quote a drop address', color: '#8b5cf6' },
        { id: 'branches', emoji: '🏬', name: 'All Branches', desc: 'List active branches', color: '#3b82f6' },
        { id: 'create-branch', emoji: '📍', name: 'Create Branch', desc: 'Add a restaurant branch', color: '#06b6d4' },
        { id: 'create-order', emoji: '➕', name: 'Create Order', desc: 'Dispatch a delivery', color: '#22c55e' },
        { id: 'list-order', emoji: '📋', name: 'List Order', desc: 'Fetch one or many orders', color: '#14b8a6' },
        { id: 'edit-order', emoji: '✏️', name: 'Edit Order', desc: 'Update a pending order', color: '#f59e0b' },
        { id: 'cancel-order', emoji: '❌', name: 'Cancel Order', desc: 'Cancel a dispatch', color: '#ef4444' },
        { id: 'balance', emoji: '💳', name: 'Wallet Balance', desc: 'Check restaurant wallet', color: '#a855f7' },
      ],
      authItems: [
        { label: 'Login API', val: 'bearerkey (in header)', color: '#f97316' },
        { label: 'All Other APIs', val: 'bearerkey (header) + token (body)', color: '#22c55e' },
        { label: 'Token lifespan', val: 'JWT — 365 days', color: '#8b5cf6' },
        { label: 'Rate limit', val: '20 requests / second', color: '#6366f1' },
      ],
    },
  },
  {
    id: 'fleet',
    slug: 'wifleet',
    name: 'WiFleet',
    headerLabel: 'Fleet API',
    logo: '/Wi_Fleet_logo.png',
    logoAlt: 'WiFleet by Wiyak',
    logoWide: true,
    tagline: 'Fleet operations',
    description: 'Manage vehicles, drivers, and fleet operations with the WiFleet API. Documentation is hosted separately and opens in a new tab.',
    color: '#7c3aed',
    emoji: '🚐',
    externalUrl: 'https://docs.wiyak.online/#quickstart',
    environments: [],
    apis: [],
    previewLinks: [
      { name: 'Quickstart', hint: 'Setup & first call' },
      { name: 'Authentication', hint: 'API keys & tokens' },
      { name: 'API reference', hint: 'Endpoints & payloads' },
      { name: 'Examples', hint: 'Sample requests' },
      { name: 'Status codes', hint: 'Error handling' },
      { name: 'Webhooks', hint: 'Live status events' },
    ],
  },
];

export function getProduct(id) {
  if (!id) return null;
  const key = String(id).toLowerCase();
  return products.find(p => p.id === key || p.slug === key) || null;
}

export function productPath(product, page) {
  if (!product) return '/';
  const slug = product.slug || product.id;
  return page && page !== 'home' ? `/${slug}/${page}` : `/${slug}`;
}
